import { ArrowLeft } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useEffect } from 'react'

const PrivacyPolicy = () => {
  const navigate = useNavigate()

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const currentDate = new Date().toLocaleDateString('es-DO', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10 lg:relative">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm sm:text-base font-medium">Volver</span>
          </button>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900">
            Política de Privacidad
          </h1>
          <p className="text-sm sm:text-base text-gray-600 mt-2">
            Última actualización: {currentDate}
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-12">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 sm:p-8 lg:p-12">
          <div className="prose prose-sm sm:prose-base lg:prose-lg max-w-none">
            
            {/* Introducción */}
            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">
                1. Introducción
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                En <strong>ColmaFácil</strong>, nos comprometemos a proteger su privacidad y sus datos personales. 
                Esta Política de Privacidad explica cómo recopilamos, utilizamos, almacenamos y protegemos su 
                información personal cuando utiliza nuestra plataforma.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                Esta política se rige por la <strong>Ley No. 172-13 sobre Protección de Datos Personales</strong> 
                de la República Dominicana y cumple con los estándares internacionales de protección de datos.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Al utilizar ColmaFácil, usted acepta las prácticas descritas en esta política. Si no está de 
                acuerdo con alguna parte de esta política, le recomendamos que no utilice nuestros servicios.
              </p>
            </section>

            {/* Responsable del Tratamiento */}
            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">
                2. Responsable del Tratamiento de Datos
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                El responsable del tratamiento de sus datos personales es:
              </p>
              <ul className="list-none space-y-2 text-gray-700 bg-gray-50 p-4 rounded-lg mb-4">
                <li className="flex items-start">
                  <strong className="mr-2 min-w-[100px]">Nombre:</strong>
                  <span>ColmaFácil</span>
                </li>
                <li className="flex items-start">
                  <strong className="mr-2 min-w-[100px]">Email:</strong>
                  <a href="mailto:info@colmafacil.store" className="text-primary-600 hover:text-primary-700">
                    colmafacil@gmail.com
                  </a>
                </li>
                <li className="flex items-start">
                  <strong className="mr-2 min-w-[100px]">Teléfono:</strong>
                  <a href="tel:+18294419998" className="text-primary-600 hover:text-primary-700">
                    +1 (829) 441-9998
                  </a>
                </li>
                <li className="flex items-start">
                  <strong className="mr-2 min-w-[100px]">Ubicación:</strong>
                  <span>República Dominicana</span>
                </li>
              </ul>
            </section>

            {/* Información que Recopilamos */}
            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">
                3. Información que Recopilamos
              </h2>
              <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-3">
                3.1. Información que Usted Nos Proporciona
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Recopilamos información que usted nos proporciona directamente:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li><strong>Datos de Registro:</strong> Nombre, apellido, correo electrónico, número de teléfono, contraseña</li>
                <li><strong>Información de Perfil:</strong> Foto de perfil, dirección de entrega, preferencias de compra</li>
                <li><strong>Información Comercial (para Colmados):</strong> Nombre del negocio, dirección comercial, número de registro, información fiscal, horarios de operación</li>
                <li><strong>Información de Pedidos:</strong> Historial de compras, productos adquiridos, direcciones de entrega, métodos de pago</li>
                <li><strong>Comunicaciones:</strong> Mensajes, consultas, comentarios y retroalimentación que nos envía</li>
              </ul>

              <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-3">
                3.2. Información Recopilada Automáticamente
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Cuando utiliza nuestra plataforma, recopilamos automáticamente:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li><strong>Datos de Dispositivo:</strong> Tipo de dispositivo, sistema operativo, identificadores únicos</li>
                <li><strong>Datos de Navegación:</strong> Dirección IP, tipo de navegador, páginas visitadas, tiempo de permanencia</li>
                <li><strong>Datos de Ubicación:</strong> Ubicación geográfica (con su consentimiento) para encontrar colmados cercanos</li>
                <li><strong>Cookies y Tecnologías Similares:</strong> Información sobre su uso de la plataforma mediante cookies, píxeles y otras tecnologías</li>
                <li><strong>Datos de Uso:</strong> Patrones de uso, funciones más utilizadas, interacciones con la plataforma</li>
              </ul>

              <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-3">
                3.3. Información de Terceros
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Podemos recibir información sobre usted de terceros, incluyendo:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>Proveedores de servicios de autenticación (Google, Facebook, etc.) si utiliza inicio de sesión social</li>
                <li>Proveedores de servicios de pago para procesar transacciones</li>
                <li>Servicios de análisis y publicidad</li>
                <li>Colmados registrados (cuando realizan pedidos o interactúan con su cuenta)</li>
              </ul>
            </section>

            {/* Uso de la Información */}
            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">
                4. Uso de la Información
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Utilizamos su información personal para los siguientes propósitos:
              </p>
              
              <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-3">
                4.1. Prestación de Servicios
              </h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>Procesar y gestionar su registro y cuenta de usuario</li>
                <li>Facilitar la conexión entre clientes y colmados</li>
                <li>Procesar pedidos, pagos y entregas</li>
                <li>Proporcionar soporte al cliente y responder a sus consultas</li>
                <li>Enviar notificaciones sobre pedidos, actualizaciones y cambios en nuestros servicios</li>
              </ul>

              <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-3">
                4.2. Mejora de la Plataforma
              </h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>Analizar el uso de la plataforma para mejorar nuestros servicios</li>
                <li>Desarrollar nuevas funcionalidades y características</li>
                <li>Personalizar su experiencia de usuario</li>
                <li>Realizar investigaciones y análisis de mercado</li>
              </ul>

              <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-3">
                4.3. Comunicación
              </h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>Enviar comunicaciones relacionadas con el servicio (confirmaciones, actualizaciones, recordatorios)</li>
                <li>Enviar boletines informativos y ofertas promocionales (con su consentimiento)</li>
                <li>Responder a sus solicitudes y comentarios</li>
                <li>Notificarle sobre cambios en nuestros términos o políticas</li>
              </ul>

              <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-3">
                4.4. Seguridad y Prevención de Fraudes
              </h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>Detectar y prevenir actividades fraudulentas, abusivas o ilegales</li>
                <li>Verificar identidades y prevenir acceso no autorizado</li>
                <li>Proteger la seguridad e integridad de nuestra plataforma</li>
                <li>Cumplir con obligaciones legales y regulatorias</li>
              </ul>

              <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-3">
                4.5. Cumplimiento Legal
              </h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>Cumplir con leyes, regulaciones y procesos legales aplicables</li>
                <li>Responder a solicitudes de autoridades gubernamentales</li>
                <li>Proteger nuestros derechos legales y los de nuestros usuarios</li>
              </ul>
            </section>

            {/* Compartir Información */}
            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">
                5. Compartir y Divulgación de Información
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                No vendemos su información personal. Compartimos su información únicamente en las siguientes 
                circunstancias:
              </p>

              <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-3">
                5.1. Con Colmados Registrados
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Cuando realiza un pedido, compartimos información necesaria con el colmado correspondiente, 
                incluyendo:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>Nombre y información de contacto para la entrega</li>
                <li>Dirección de entrega</li>
                <li>Detalles del pedido (productos, cantidades, preferencias especiales)</li>
                <li>Información de pago necesaria para procesar la transacción</li>
              </ul>

              <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-3">
                5.2. Con Proveedores de Servicios
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Compartimos información con proveedores de servicios que nos ayudan a operar nuestra plataforma:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>Proveedores de hosting y servicios en la nube</li>
                <li>Proveedores de servicios de pago</li>
                <li>Servicios de análisis y marketing</li>
                <li>Proveedores de servicios de comunicación (email, SMS)</li>
                <li>Proveedores de servicios de soporte al cliente</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mb-4">
                Estos proveedores están contractualmente obligados a proteger su información y solo pueden 
                utilizarla para los fines específicos para los que fueron contratados.
              </p>

              <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-3">
                5.3. Por Requisitos Legales
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Podemos divulgar su información si es requerido por ley o en respuesta a:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>Órdenes judiciales, citaciones o procesos legales</li>
                <li>Solicitudes de autoridades gubernamentales</li>
                <li>Protección de nuestros derechos legales</li>
                <li>Prevención de fraude o actividades ilegales</li>
                <li>Protección de la seguridad de nuestros usuarios</li>
              </ul>

              <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-3">
                5.4. Con Su Consentimiento
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Compartiremos su información con terceros cuando usted nos haya dado su consentimiento explícito 
                para hacerlo.
              </p>
            </section>

            {/* Cookies y Tecnologías de Seguimiento */}
            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">
                6. Cookies y Tecnologías de Seguimiento
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Utilizamos cookies y tecnologías similares para mejorar su experiencia, analizar el uso de la 
                plataforma y personalizar contenido. Los tipos de cookies que utilizamos incluyen:
              </p>
              
              <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-3">
                6.1. Tipos de Cookies
              </h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li><strong>Cookies Esenciales:</strong> Necesarias para el funcionamiento básico de la plataforma</li>
                <li><strong>Cookies de Funcionalidad:</strong> Permiten recordar sus preferencias y configuraciones</li>
                <li><strong>Cookies Analíticas:</strong> Nos ayudan a entender cómo los usuarios interactúan con la plataforma</li>
                <li><strong>Cookies de Marketing:</strong> Utilizadas para mostrar contenido relevante y medir la efectividad de campañas</li>
              </ul>

              <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-3">
                6.2. Gestión de Cookies
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Puede gestionar sus preferencias de cookies a través de la configuración de su navegador. 
                Sin embargo, desactivar ciertas cookies puede afectar la funcionalidad de la plataforma.
              </p>
            </section>

            {/* Seguridad de los Datos */}
            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">
                7. Seguridad de los Datos
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Implementamos medidas de seguridad técnicas, administrativas y físicas para proteger su información 
                personal contra acceso no autorizado, alteración, divulgación o destrucción:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li><strong>Cifrado:</strong> Utilizamos cifrado SSL/TLS para proteger la transmisión de datos</li>
                <li><strong>Almacenamiento Seguro:</strong> Sus datos se almacenan en servidores seguros con medidas de protección avanzadas</li>
                <li><strong>Control de Acceso:</strong> Limitamos el acceso a su información personal solo al personal autorizado</li>
                <li><strong>Autenticación:</strong> Implementamos sistemas de autenticación robustos para proteger su cuenta</li>
                <li><strong>Monitoreo:</strong> Monitoreamos continuamente nuestros sistemas para detectar y prevenir amenazas</li>
                <li><strong>Actualizaciones de Seguridad:</strong> Mantenemos nuestros sistemas actualizados con las últimas medidas de seguridad</li>
              </ul>
              <p className="text-gray-700 leading-relaxed">
                A pesar de nuestros esfuerzos, ningún método de transmisión o almacenamiento es 100% seguro. 
                No podemos garantizar la seguridad absoluta de su información, pero nos comprometemos a notificarle 
                de cualquier violación de seguridad que pueda afectar sus datos personales.
              </p>
            </section>

            {/* Retención de Datos */}
            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">
                8. Retención de Datos
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Conservamos su información personal durante el tiempo necesario para:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>Proporcionar nuestros servicios mientras su cuenta esté activa</li>
                <li>Cumplir con obligaciones legales, regulatorias o contractuales</li>
                <li>Resolver disputas y hacer cumplir nuestros acuerdos</li>
                <li>Mantener registros para fines de seguridad y prevención de fraude</li>
              </ul>
              <p className="text-gray-700 leading-relaxed">
                Cuando su información ya no sea necesaria para estos propósitos, la eliminaremos de forma segura 
                o la anonimizaremos, excepto cuando la ley requiera que la conservemos por períodos más largos.
              </p>
            </section>

            {/* Sus Derechos */}
            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">
                9. Sus Derechos sobre sus Datos Personales
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                De acuerdo con la Ley No. 172-13 de República Dominicana, usted tiene los siguientes derechos:
              </p>

              <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-3">
                9.1. Derecho de Acceso
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Tiene derecho a acceder y obtener una copia de sus datos personales que tenemos en nuestro poder.
              </p>

              <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-3">
                9.2. Derecho de Rectificación
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Puede solicitar la corrección de datos inexactos o incompletos.
              </p>

              <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-3">
                9.3. Derecho de Cancelación (Derecho al Olvido)
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Puede solicitar la eliminación de sus datos personales cuando ya no sean necesarios o cuando 
                retire su consentimiento, sujeto a ciertas excepciones legales.
              </p>

              <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-3">
                9.4. Derecho de Oposición
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Puede oponerse al tratamiento de sus datos personales para ciertos fines, como marketing directo.
              </p>

              <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-3">
                9.5. Derecho de Portabilidad
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Puede solicitar recibir sus datos personales en un formato estructurado y de uso común.
              </p>

              <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-3">
                9.6. Derecho de Revocación del Consentimiento
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Puede revocar su consentimiento para el tratamiento de datos en cualquier momento, sin afectar 
                la legalidad del tratamiento basado en el consentimiento previo.
              </p>

              <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-3">
                9.7. Cómo Ejercer sus Derechos
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Para ejercer cualquiera de estos derechos, puede contactarnos a través de:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>Email: <a href="mailto:info@colmafacil.store" className="text-primary-600 hover:text-primary-700">colmafacil@gmail.com</a></li>
                <li>Teléfono: <a href="tel:+18294419998" className="text-primary-600 hover:text-primary-700">+1 (829) 441-9998</a></li>
                <li>Configuración de su cuenta en la plataforma</li>
              </ul>
              <p className="text-gray-700 leading-relaxed">
                Responderemos a su solicitud dentro de un plazo razonable y de acuerdo con los plazos establecidos 
                por la ley.
              </p>
            </section>

            {/* Transferencias Internacionales */}
            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">
                10. Transferencias Internacionales de Datos
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Sus datos personales pueden ser transferidos y almacenados en servidores ubicados fuera de 
                República Dominicana. Cuando realizamos transferencias internacionales, nos aseguramos de que:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>Los países receptores tengan leyes adecuadas de protección de datos</li>
                <li>Implementemos medidas contractuales apropiadas para proteger sus datos</li>
                <li>Obtengamos su consentimiento cuando sea requerido por ley</li>
              </ul>
            </section>

            {/* Privacidad de Menores */}
            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">
                11. Privacidad de Menores
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Nuestros servicios están dirigidos a personas mayores de 18 años. No recopilamos intencionalmente 
                información personal de menores de edad sin el consentimiento de sus padres o tutores legales.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Si descubrimos que hemos recopilado información de un menor sin el consentimiento apropiado, 
                tomaremos medidas para eliminar esa información de nuestros sistemas. Si es padre o tutor y 
                cree que su hijo nos ha proporcionado información personal, contáctenos inmediatamente.
              </p>
            </section>

            {/* Enlaces a Terceros */}
            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">
                12. Enlaces a Sitios Web de Terceros
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Nuestra plataforma puede contener enlaces a sitios web de terceros. No somos responsables de las 
                prácticas de privacidad o el contenido de estos sitios. Le recomendamos revisar las políticas 
                de privacidad de cualquier sitio web de terceros que visite.
              </p>
            </section>

            {/* Cambios a esta Política */}
            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">
                13. Cambios a esta Política de Privacidad
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Podemos actualizar esta Política de Privacidad ocasionalmente para reflejar cambios en nuestras 
                prácticas o por razones legales, operativas o regulatorias. Le notificaremos sobre cambios 
                significativos mediante:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>Notificación prominente en nuestra plataforma</li>
                <li>Correo electrónico a la dirección registrada</li>
                <li>Actualización de la fecha de "Última actualización"</li>
              </ul>
              <p className="text-gray-700 leading-relaxed">
                Le recomendamos revisar esta política periódicamente para mantenerse informado sobre cómo 
                protegemos su información.
              </p>
            </section>

            {/* Contacto */}
            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">
                14. Contacto
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Si tiene preguntas, inquietudes o solicitudes relacionadas con esta Política de Privacidad o 
                el tratamiento de sus datos personales, puede contactarnos:
              </p>
              <ul className="list-none space-y-2 text-gray-700 bg-gray-50 p-4 rounded-lg">
                <li className="flex items-start">
                  <strong className="mr-2 min-w-[100px]">Email:</strong>
                  <a href="mailto:info@colmafacil.store" className="text-primary-600 hover:text-primary-700">
                    colmafacil@gmail.com
                  </a>
                </li>
                <li className="flex items-start">
                  <strong className="mr-2 min-w-[100px]">Teléfono:</strong>
                  <a href="tel:+18294419998" className="text-primary-600 hover:text-primary-700">
                    +1 (829) 441-9998
                  </a>
                </li>
                <li className="flex items-start">
                  <strong className="mr-2 min-w-[100px]">Ubicación:</strong>
                  <span>República Dominicana</span>
                </li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                También tiene derecho a presentar una queja ante la autoridad de protección de datos competente 
                si considera que el tratamiento de sus datos personales viola la ley aplicable.
              </p>
            </section>

            {/* Consentimiento */}
            <section className="mt-12 pt-8 border-t border-gray-200">
              <p className="text-gray-700 leading-relaxed text-center">
                <strong>Al utilizar ColmaFácil, usted reconoce haber leído, entendido y aceptado esta 
                Política de Privacidad y consiente el tratamiento de sus datos personales según se describe 
                en esta política.</strong>
              </p>
            </section>

          </div>
        </div>
      </div>
    </div>
  )
}

export default PrivacyPolicy

